import { useCallback, useEffect, useRef, useState } from 'react';
import { GameFrame, PromptLine, pick, shuffle, splitFace, useGame, useRoundTimer, type GameProps } from '../sdk/react';
import { Chip, DropSpot, usePlacer } from '../kit/place';
import { KINDS } from '../../concepts/kinds';
import { Pic } from '../sdk/react';

type Row = { label: string; items: string[] };
type Piece = { key: string; text: string };

export default function SlotOrder(props: GameProps) {
  const game = useGame(props);
  const rows = ((game.config?.sequences ?? []) as Row[]).filter((r) => r?.label && Array.isArray(r.items) && r.items.length >= 2);
  const speakCards = game.bool('chipSpeech', true);
  const [row, setRow] = useState<Row | null>(null);
  const [pieces, setPieces] = useState<Piece[]>([]);
  const [slots, setSlots] = useState<(Piece | null)[]>([]);
  const [trials, setTrials] = useState<string[]>([]);
  const [bounce, setBounce] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const busy = useRef(false);

  const start = useCallback(async () => {
    if (rows.length === 0) return;
    const next = pick(rows);
    const ps = next.items.map((text, i) => ({ key: `${i}`, text }));
    setDone(false);
    const ids: string[] = [];
    for (let i = 0; i < next.items.length; i++) {
      const id = await game.register({ label: next.label, slot: i + 1, pieces: next.items }, { value: next.items[i] });
      if (!id) return;
      ids.push(id);
    }
    setRow(next);
    setPieces(shuffle(ps));
    setSlots(next.items.map(() => null));
    setTrials(ids);
  }, [rows.map((r) => r.label + r.items.join()).join('|')]);

  useEffect(() => { if (game.config) void start(); }, [game.config, start]);
  useEffect(() => {
    if (!row || game.textPrompt) return;
    const t = window.setTimeout(() => game.say(row.label), 300);
    return () => window.clearTimeout(t);
  }, [row]);

  const placer = usePlacer((pieceKey, slotId) => { void drop(pieceKey, Number(slotId.slice(5))); });

  async function drop(pieceKey: string, slot: number) {
    if (!row || busy.current || slots[slot]) return;
    const piece = pieces.find((p) => p.key === pieceKey);
    if (!piece) return;
    const right = row.items[slot];
    if (game.errorless && piece.text !== right) { setBounce(pieceKey); window.setTimeout(() => setBounce(null), 400); return; }
    busy.current = true;
    const v = await game.respond(trials[slot], piece.text);
    if (v?.correct) {
      const next = [...slots];
      next[slot] = piece;
      setSlots(next);
      busy.current = false;
      if (next.every(Boolean)) setDone(true);
      return;
    }
    setBounce(pieceKey);
    window.setTimeout(() => setBounce(null), 400);
    const fresh = await game.register({ label: row.label, slot: slot + 1, retry: true }, { value: right });
    busy.current = false;
    if (fresh) setTrials((t) => t.map((x, i) => (i === slot ? fresh : x)));
  }

  const fading = useRoundTimer(done, game.transitionMs, () => { void start(); }, 1200);
  const used = new Set(slots.filter(Boolean).map((p) => p!.key));

  return (
    <GameFrame game={game} empty={rows.length === 0} emptyText={`${KINDS.slotOrder.describes} Edit it to add something to build.`} onResume={() => { void start(); }} breakTrial={trials[0] ?? null}>
      {row && (
        <div className={`g-board g-slots${fading ? ' is-fading' : ''}`}>
          <PromptLine game={game} text={row.label} />
          <div className="g-slot-row" role="group" aria-label="Spaces">
            {slots.map((p, i) => (
              p ? (
                <span key={i} className="g-slot is-filled">{(() => { const f = splitFace(p.text); return <><Pic text={p.text} fallback={f.picture ? <span aria-hidden="true">{f.picture}</span> : null} className="g-slot-img" />{f.word && <span>{f.word}</span>}</>; })()}</span>
              ) : (
                <DropSpot key={i} placer={placer} id={`slot-${i}`} label={`Space ${i + 1}`} className="g-slot">
                  <span className="g-slot-n">{i + 1}</span>
                </DropSpot>
              )
            ))}
          </div>
          <div className="g-tray" role="group" aria-label="Pieces">
            {pieces.filter((p) => !used.has(p.key)).map((p) => {
              const f = splitFace(p.text);
              return (
                <Chip key={p.key} placer={placer} id={p.key} label={f.word || p.text} className={bounce === p.key ? 'is-bounce' : ''} onTap={() => { if (speakCards) game.say(f.word || p.text); }}>
                  <Pic text={p.text} fallback={f.picture ? <span className="g-chip-pic g-chip-emoji" aria-hidden="true">{f.picture}</span> : null} className="g-chip-pic" />
                  {f.word && <span className="g-chip-word">{f.word}</span>}
                </Chip>
              );
            })}
          </div>
        </div>
      )}
    </GameFrame>
  );
}
